import { inject, Injectable, signal } from '@angular/core';
import {
  CategoriesResponse,
  CategoryProductStatusesResponse,
} from '../../../../../utils/interfaces/categories.interface';
import { Observable, tap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  CreateCategoryDto,
  Category,
  GetCategoriesDto,
} from '../../../../../utils/interfaces/categories.interface';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PARENT_ID,
  DEFAULT_SHOP_ID,
  DEFAULT_VISIBILITY,
} from '../constants';
import { ApiEndpoints } from '@app/utils/enums/api-endpoints';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class CategoriesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;
  public categories = signal<Category[]>([]);

  public getCategories({
    pageNumber = DEFAULT_PAGE_NUMBER,
    pageSize = DEFAULT_PAGE_SIZE,
    shopId = DEFAULT_SHOP_ID,
  }: GetCategoriesDto): Observable<CategoriesResponse> {
    const params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('PageSize', pageSize)
      .set('ShopId', shopId);
    return this.http
      .get<CategoriesResponse>(`${this.baseUrl}${ApiEndpoints.CATEGORIES}`, { params })
      .pipe(
        tap((res) => {
          const categories = this.categories();
          this.categories.set([...categories, ...res.data.categories]);
        }),
      );
  }

  public getCategoryProductStatuses(
    categoryId: number,
  ): Observable<CategoryProductStatusesResponse> {
    return this.http.get<CategoryProductStatusesResponse>(
      `${this.baseUrl}${ApiEndpoints.GET_CATEGORY_PRODUCT_STATUSES}/${categoryId}`,
    );
  }

  public createNewCategory(req: CreateCategoryDto): Observable<CategoriesResponse> {
    return this.http.post<CategoriesResponse>(`${this.baseUrl}${ApiEndpoints.CATEGORIES}`, req);
  }

  public createCategory(dto: CreateCategoryDto): Observable<Category> {
    const form = this.buildCategoryForm(dto);

    return this.http.post<Category>(`${this.baseUrl}${ApiEndpoints.CATEGORIES}`, form);
  }

  private buildCategoryForm({
    name = '',
    url = '',
    visibility = DEFAULT_VISIBILITY,
    parentId = DEFAULT_PARENT_ID,
    shopId = DEFAULT_SHOP_ID,
  }: CreateCategoryDto): FormData {
    const form = new FormData();
    const parentIdValue = parentId === null ? parentId : parentId.toString();

    form.append('name', name);
    form.append('url', url);
    form.append('visibility', visibility.toString());
    form.append('parentId', parentIdValue);
    form.append('shopId', shopId.toString());

    return form;
  }

  public updateCategory(id: number, data: Partial<Category>): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}${ApiEndpoints.CATEGORIES}/${id}`, data);
  }

  public deleteCategory(categoryId: number): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}${ApiEndpoints.CATEGORIES}/${categoryId}`);
  }
}
